import {CHANGE_GROUP_CODE_PATTERN, CHANGE_GROUP_NAME_PREFIX} from "../config";
import {ICSAncillary} from "../../../models/models";

/**
 * LPP-22293 / US-NTWRA-005 (RDT §3 SCFE-1, §2.1) — logica pura di raggruppamento del
 * servizio "Change Group".
 *
 * Le ancillary del gruppo sono identificate dal pattern FE `^CG\d+$` sul `ancillaryCode`
 * (in subordine dal `name` che inizia con "Change Group"). La logica è priva di dipendenze
 * da React/recoil così da essere riusabile dal componente e verificabile a unità.
 *
 * L'accorpamento è di sola presentazione: la selezione/deselezione nel controllo a scelta
 * multipla valorizza le stesse `ICSAncillary` sottostanti (units 1/0), senza alterare
 * payload counter-sale, prezzo o totale (RF-07, Requirement 2).
 */

/**
 * Sottoinsieme (strutturale) dei campi necessari a riconoscere un Change Group.
 *
 * Definito come interfaccia con campi opzionali — non come `Pick<ICSAncillary, ...>` — così
 * da essere soddisfatto sia da `ICSAncillary` (create-ra) sia da `IRADAncillary`
 * (ra-detail/ra-edit), che espone il nome di default come `ancillaryDefaultName`. Questo
 * mantiene la logica di raggruppamento in UN SOLO punto (Requirement 4), riusata da tutte
 * le viste senza duplicazioni.
 */
export interface TChangeGroupCandidate {
	ancillaryCode?: string | null;
	ancillaryNameIt?: string | null;
	ancillaryNameEn?: string | null;
	ancillaryDefaultLabel?: string | null;
	ancillaryDefaultName?: string | null;
}

/**
 * @returns true se l'ancillary appartiene al gruppo "Change Group".
 * Criterio primario: `ancillaryCode` matcha `^CG\d+$`. Criterio subordinato: uno dei nomi
 * inizia con "Change Group".
 */
export const isChangeGroupAncillary = (ancillary?: TChangeGroupCandidate | null): boolean => {
	if (!ancillary) {
		return false;
	}
	const code = (ancillary.ancillaryCode ?? "").trim();
	if (CHANGE_GROUP_CODE_PATTERN.test(code)) {
		return true;
	}
	const prefix = CHANGE_GROUP_NAME_PREFIX.toLowerCase();
	return [
		ancillary.ancillaryNameIt,
		ancillary.ancillaryNameEn,
		ancillary.ancillaryDefaultLabel,
		ancillary.ancillaryDefaultName,
	].some((name) => typeof name === "string" && name.trim().toLowerCase().startsWith(prefix));
};

/** Elemento ancillary con l'indice originale nella lista (necessario per il binding al form). */
export interface IIndexedAncillary<T extends TChangeGroupCandidate = ICSAncillary> {
	ancillary: T;
	/** posizione originale nell'array `booked`/`counterSale` (usata dal path del form) */
	index: number;
}

export interface IChangeGroupPartition<T extends TChangeGroupCandidate = ICSAncillary> {
	/** ancillary Change Group (`^CG\d+$`), da renderizzare come singolo controllo multi-scelta */
	changeGroup: IIndexedAncillary<T>[];
	/** tutte le altre ancillary, invariate (box separati) */
	others: IIndexedAncillary<T>[];
}

/**
 * Partiziona una lista di ancillary in "Change Group" e "altre", preservando l'indice
 * originale di ciascun elemento (il path del form dipende dalla posizione nell'array).
 * Generica sul tipo ancillary per essere riusata da create-ra (`ICSAncillary`) e dalle
 * viste ra-detail/ra-edit (`IRADAncillary`).
 */
export const partitionChangeGroup = <T extends TChangeGroupCandidate = ICSAncillary>(
	list: T[] = []
): IChangeGroupPartition<T> => {
	const changeGroup: IIndexedAncillary<T>[] = [];
	const others: IIndexedAncillary<T>[] = [];
	(list ?? []).forEach((ancillary, index) => {
		if (isChangeGroupAncillary(ancillary)) {
			changeGroup.push({ancillary, index});
		} else {
			others.push({ancillary, index});
		}
	});
	return {changeGroup, others};
};

/** @returns true se l'ancillary è selezionata (units booleano true, oppure units/mileage > 0). */
export const isAncillarySelected = (ancillary?: ICSAncillary | null): boolean => {
	if (!ancillary) {
		return false;
	}
	if (typeof ancillary.units === "boolean") {
		return ancillary.units;
	}
	return Number(ancillary.units ?? 0) > 0 || Number(ancillary.mileage ?? 0) > 0;
};

/** Codici delle ancillary Change Group attualmente selezionate in una lista. */
export const getSelectedChangeGroupCodes = (list: ICSAncillary[] = []): string[] =>
	partitionChangeGroup(list)
		.changeGroup.filter(({ancillary}) => isAncillarySelected(ancillary))
		.map(({ancillary}) => ancillary.ancillaryCode);

/**
 * Applica una selezione di codici Change Group a una lista di ancillary, ritornando una
 * NUOVA lista: le ancillary CG assumono `units = 1` se selezionate, `units = 0` altrimenti;
 * tutte le altre ancillary restano identiche. È la stessa operazione dei box a checkbox
 * (units 1/0 per code) — garantisce l'invarianza del payload a parità di selezione.
 */
export const applyChangeGroupSelection = (
	list: ICSAncillary[] = [],
	selectedCodes: string[] = []
): ICSAncillary[] => {
	const selected = new Set(selectedCodes);
	return (list ?? []).map((ancillary) => {
		if (!isChangeGroupAncillary(ancillary)) {
			return ancillary;
		}
		return {...ancillary, units: selected.has(ancillary.ancillaryCode) ? 1 : 0};
	});
};
